import { useFieldArray } from 'react-final-form-arrays';
import { useCameraViewContext } from '../../context';
import styled, { css, keyframes } from 'styled-components';
import { useRef, useState } from 'react';
import { Field, FieldInputProps, useField } from 'react-final-form';
import { CollapseIcon } from 'components/Icons/CollapseIcon';
import { ExpandIcon } from 'components/Icons/ExpandIcon';
import { BinIcon } from 'components/Icons';
import { TiresImageCarousel } from 'pages/ListerVehicleUploadV2/components/tires/tiresImageCarousel';
import { useHorizontalScrollFade } from 'hooks';
import { useDamageImageFields, useDamageImages } from 'pages/ListerVehicleUploadV2/hooks/useDamagedImages';
import { cameraTypeToIcon, typeToLabel } from 'pages/ListerVehicleUploadV2/components/photos/constants';
import { VideoComponent } from './VideoComponent';
import { useRemoveImage } from 'pages/ListerVehicleUploadV2/hooks/useRemoveImage';
import { useBackgroundImage } from 'pages/ListerVehicleUploadV2/hooks/useBackgroundImage';
import { toggleImageListStatus, useImageListOpened } from 'services/listerUploadVehicle/v2';
import { useDispatch } from 'react-redux';
import { CircularProgress, Icon } from '@material-ui/core';
import { Image, PlaceholderContainer, PlaceholderWrapper } from './ui';
import { GoBackPlaceholder } from './GoBackPlaceholder';
import { useUpdateImage } from 'pages/ListerVehicleUploadV2/hooks/useUpdateImage';
import { parsePath } from 'pages/ListerVehicleUploadV2/components/notes/utils';
import { useImageListScroll } from '../../hooks/useImageListScroll';

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;

  @media (orientation: landscape) {
    margin-top: 4px;
  }
`;

const Collapse = keyframes`
  0% { 
    clip-path: inset(0 0 0 0);
    opacity: 1;
    background: transparent;
  }
  1% {
    clip-path: inset(0 0 0 0);
    opacity: 1;
    background: rgba(0, 0, 0, 0.8);
  }
  100% { 
    clip-path: inset(0 0 100% 0);
    background: rgba(0, 0, 0, 0.8);
    opacity: 0;
  }
`;

const Expand = keyframes`
  0% { 
    clip-path: inset(0 0 100% 0);
    opacity: 0;
  }
  100% { 
    clip-path: inset(0 0 0 0);
    opacity: 1;
  }
`;

const ContainerWrapper = styled.div<{ isExpanded: boolean }>`
  position: relative;
  padding-bottom: 12px;

  opacity: ${props => (props.isExpanded ? 1 : 0)};

  @media (orientation: landscape) {
    background: ${props => (props.isExpanded ? 'rgba(0, 0, 0, 0.8)' : 'transparent')};
    padding: 0 12px 4px calc(12px + env(safe-area-inset-left, 0));
  }

  &.collapse {
    animation: ${Collapse} 0.3s ease-in-out;
  }

  &.expand {
    animation: ${Expand} 0.3s ease-in-out;
  }

  @media (orientation: landscape) {
    min-height: 92px;
    animation: none !important;
  }
`;

/**
 *  grid-template-columns: repeat(auto-fill, 97px);
  grid-auto-columns: 97px;
 */

const Container = styled.div<{ showAfterFade: boolean; showBeforeFade: boolean }>`
  margin: 0;
  padding: 0;
  overflow: hidden;
  overflow-x: auto;
  width: 100%;
  display: flex;
  gap: 8px;

  &:before {
    content: '';
    display: ${props => (props.showBeforeFade ? 'block' : 'none')};
    top: 0;
    left: 0;
    width: 100px;
    position: absolute;
    z-index: 999;
    height: 100%;
    pointer-events: none;
    background: linear-gradient(90deg, rgb(0, 0, 0) 0%, rgba(0, 0, 0, 0) 100%);
  }

  &:after {
    content: '';
    display: ${props => (props.showAfterFade ? 'block' : 'none')};
    right: 0;
    top: 0;
    width: 100px;
    position: absolute;
    height: 100%;
    pointer-events: none;
    background: linear-gradient(90deg, rgba(0, 0, 0, 0) 0%, #000000 100%);
  }

  &::-webkit-scrollbar {
    display: none;
  }
`;

const ControlsRow = styled.div`
  padding: 8px 24px 8px 24px;
  display: flex;
  gap: 16px;
  align-items: center;

  @media (orientation: landscape) {
    background: rgba(0, 0, 0, 0.8);
  }
`;

const ControlsLabel = styled.div`
  color: #cccccc;
  font-weight: 400;
  font-size: 14px;
  line-height: 16.41px;
`;

const CountLabel = styled(ControlsLabel)`
  margin-left: auto;
`;

const IconWrapper = styled.div`
  display: flex;
`;

const ProgressWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

export const ImageListController = () => {
  const { level1SectionId, imageIndex } = useCameraViewContext();
  const isExpanded = useImageListOpened();
  const dispatch = useDispatch();
  const toggleImageList = () => {
    dispatch(toggleImageListStatus());
  };
  const { fields } = useFieldArray(`${level1SectionId}.images`, { subscription: { value: true, length: true } });
  const [additionalClassName, setAdditionalClassName] = useState('');

  const showVideo = level1SectionId === 'exterior.subsections.exterior';

  const toggleExpanded = () => {
    if (isExpanded) {
      setAdditionalClassName('collapse');
    } else {
      setAdditionalClassName('expand');
    }
    toggleImageList();
  };

  return (
    <PageWrapper>
      <ControlsRow>
        <IconWrapper onClick={toggleExpanded}>
          {isExpanded ? <CollapseIcon fillColor={'#fff'} /> : <ExpandIcon fillColor={'#fff'} />}
        </IconWrapper>
        <ControlsLabel>Photos</ControlsLabel>
        <CountLabel>{`${imageIndex + 1}/${level1SectionId === 'drive_cycles' ? 1 : 50}`}</CountLabel>
      </ControlsRow>
      <ContainerWrapper
        isExpanded={isExpanded && (fields.length! > 0 || showVideo)}
        className={additionalClassName}
        onAnimationEnd={() => setAdditionalClassName('')}
      >
        {/* <DefaultImageList /> */}
        {level1SectionId?.includes('tires') ? <TiresImageCarousel /> : <DefaultImageList />}
      </ContainerWrapper>
    </PageWrapper>
  );
};

export const DamagedListController = () => {
  const damagedImages = useDamageImageFields();

  const ref = useRef<HTMLDivElement>(null);

  const { showBeforeFade, showAfterFade } = useHorizontalScrollFade(ref, {
    beforeFadeThreshold: 50
  });

  return (
    <Container ref={ref} showBeforeFade={showBeforeFade} showAfterFade={showAfterFade}>
      {damagedImages.map(image => {
        return <DamagedImage imageId={image.name} key={image.name} />;
      })}
    </Container>
  );
};

const DamagedImage = ({ imageId }: { imageId: string }) => {
  const {
    input: { value: image }
  } = useField(imageId);

  const { input: damagedInput } = useField(`${imageId}.is_damaged`);

  const imageUrl = useBackgroundImage({
    localUrl: image.local_url,
    serverUrl: image.url
  });

  const [sectionId, i] = parsePath(imageId);
  const updateImage = useUpdateImage(sectionId, i);

  const handleClick = () => {
    damagedInput.onChange(false);
    updateImage({ is_damaged: false });
  };

  if (!imageUrl) return null;
  return (
    <Image isPlaceholder={false} imageUrl={imageUrl}>
      <BinIconWrapper onClick={handleClick}>
        <BinIcon />
      </BinIconWrapper>
    </Image>
  );
};

const DefaultImageList = () => {
  const ref = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<HTMLDivElement[]>([]);

  useImageListScroll(ref, itemRefs);

  const { level1SectionId, isVideoMode } = useCameraViewContext();
  const { fields } = useFieldArray(`${level1SectionId}.images`);

  const { showBeforeFade, showAfterFade } = useHorizontalScrollFade(ref, {
    beforeFadeThreshold: 50
  });

  const showVideo = level1SectionId === 'exterior.subsections.exterior';

  return (
    <Container ref={ref} showBeforeFade={showBeforeFade} showAfterFade={showAfterFade}>
      {fields.map((field, i) => {
        if (!fields.value[i].url && !fields.value[i].local_url && !fields.value[i].type) return null;
        return (
          <Field name={field} key={field}>
            {fieldProps => <ImageComponent setRef={el => (itemRefs.current[i] = el)} index={i} {...fieldProps.input} />}
          </Field>
        );
      })}
      {showVideo && (
        <>
          {isVideoMode && <GoBackPlaceholder />}
          <Field name={'exterior.subsections.exterior.video'}>
            {fieldProps => <VideoComponent containerRef={ref} {...fieldProps.input} />}
          </Field>
        </>
      )}
    </Container>
  );
};

const BinIconWrapper = styled.div`
  position: absolute;
  width: 18px;
  height: 18px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  top: 4px;
  right: 4.6px;
  & svg {
    width: 12px;
    height: 12px;
  }
`;

const Label = styled.div`
  font-weight: 400;
  font-size: 10px;
  line-height: 100%;
  text-align: center;
  color: #fff;
  padding: 0 8px;
  flex-basis: 25%;
`;

const RowLabelContainer = styled.div`
  position: absolute;
  bottom: 8px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 10px;
  font-weight: 700;
  line-height: 100%;
  color: #fff;
  z-index: 100;
`;

export const ImageComponent = ({
  index,
  setRef,
  ...input
}: FieldInputProps<any, HTMLElement> & { index: number; setRef?: any }) => {
  const { level1SectionId, setIndex, localRemoveImage, imageIndex, selectPhoto } = useCameraViewContext();
  const { fields } = useFieldArray(`${level1SectionId}.images`);

  const {
    input: { onChange: setLocalUrl }
  } = useField(`${level1SectionId}.images.${index}.local_url`);

  const removeImage = useRemoveImage();

  const imageUrl = useBackgroundImage({
    localUrl: input.value.local_url,
    serverUrl: input.value.url,
    onServerLoaded: () => {
      if (input.value.local_url) {
        URL.revokeObjectURL(input.value.local_url);
        setLocalUrl('');
        // fields.update(index, { ...(fields.value[index] || {}), local_url: '' });
      }
    }
  });

  const handleRemove = async (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.stopPropagation();
    e.preventDefault();
    const afterRemove = () => {
      console.log('AFTER REMOVE', input.value);
      if (input.value.type) {
        fields.update(index, { ...input.value, url: '', url_thumb: '', local_url: '', delete_scheduled: true });
      } else {
        fields.remove(index);
        localRemoveImage();
      }
    };
    await removeImage(input.value.id, !!input.value.type, afterRemove);
  };

  const isPlaceholder = !imageUrl;
  const handleSelect = () => {
    if (imageUrl) {
      selectPhoto(index);
    } else {
      setIndex(index);
    }
  };

  const label = input.value.type ? typeToLabel[input.value.type] : null;

  const isLoading = input.value?.status === 'uploading';
  const isFailed = input.value?.status === 'failed';

  const retryUpload = () => {
    // need to get an image from somewhere
    // uploadImage(input.value.local_url);
  };

  return (
    <Image
      ref={setRef}
      isLoading={isLoading}
      onClick={handleSelect}
      isPlaceholder={!imageUrl}
      imageUrl={imageUrl}
      isFailed={isFailed}
      isSelected={index === imageIndex}
    >
      {!isPlaceholder && (
        <BinIconWrapper onClick={handleRemove}>
          <BinIcon />
        </BinIconWrapper>
      )}
      {isPlaceholder && !!input.value.type && (
        <PlaceholderContainer>
          <PlaceholderWrapper withLabel={!!label}>{cameraTypeToIcon[input.value.type]}</PlaceholderWrapper>
          {!!label && <Label>{label}</Label>}
        </PlaceholderContainer>
      )}
      {isLoading && (
        <ProgressWrapper>
          <CircularProgress />
        </ProgressWrapper>
      )}
      {isFailed && (
        <ProgressWrapper onClick={retryUpload}>
          <Icon style={{ fontSize: '40px', color: '#D01C2E' }}>error</Icon>
        </ProgressWrapper>
      )}
      {!isPlaceholder && label && <RowLabelContainer>{label}</RowLabelContainer>}
    </Image>
  );
};
